import {createApp} from 'vue'
import {createPinia} from 'pinia'
import mitt from 'mitt'
import {pusher} from "./pusher/index"
import {DateTime} from "luxon"
import VueGoogleMaps from 'vue-google-maps-community-fork'
import {VERSION} from "./code/app";
import "./styles/app.css"
import "./styles/tailwind.css"
import {definePreset} from "@primeuix/themes";
import Lara from '@primeuix/themes/lara';

const MyPrimeVuePreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#fff1f2',
      100: '#ffe3e5',
      200: '#ffccd2',
      300: '#ffa2ad',
      400: '#fe6e82',
      500: '#f83b5a',
      600: '#e51945',
      700: '#c20e39',
      800: '#a20f37',
      900: '#8b1035',
      950: '#5b041c',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.375rem',
    },
    list: {
      paddingX: '0.75rem',
      paddingY: '1.375rem',
    },
    colorScheme: {
      light: {
        surface: {
          50: 'oklch(0.985 0 0)',
          100: 'oklch(0.97 0 0)',
          200: 'oklch(0.922 0 0)',
          300: 'oklch(0.87 0 0)',
          400: 'oklch(0.708 0 0)',
          500: 'oklch(0.556 0 0)',
          600: 'oklch(0.439 0 0)',
          700: 'oklch(0.371 0 0)',
          800: 'oklch(0.269 0 0)',
          900: 'oklch(0.205 0 0)',
          950: 'oklch(0.145 0 0)',
        },
        primary: {
          color: '{primary.800}',
          inverseColor: '#ffffff',
          hoverColor: '{primary.900}',
          activeColor: '{primary.800}',
        },
        formField: {
          hoverBorderColor: '{surface.300}'
        },
        list: {
          option: {
            focusBackground: '{primary.700}',
            focusColor: '#ffffff',
          }
        },
        highlight: {
          background: '{primary.900}',
          focusBackground: '{primary.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
        focusRing: {
          width: '2px',
          style: 'solid',
          color: '{primary.color}',
          offset: '0',
          shadow: null
        },
      }
    }
  }
});

const emitter = mitt();
const theme = getEnv('VITE_THEME') || 'csa';

import App from './App.vue'
import PrimeVue from 'primevue/config';
import router from './router'
import axios from "axios";
import FormError from "./components/utils/FormError.vue";
import Paginador from "./components/utils/Paginador.vue";
import FlashAlert from "./components/utils/FlashAlert.vue";
import DialogoCargando from "./components/utils/DialogoCargando.vue";
import LabelTextCounter from "./components/utils/LabelTextCounter.vue";
import permisos from './permisos'
import configuraciones from './code/configuraciones.js'
import ToastService from "primevue/toastservice";

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.baseURL = getEnv('VITE_SERVER_URL');
axios.defaults.headers.common['Accept'] = 'application/json';

axios.get('/sanctum/csrf-cookie');

window.axios = axios;

const app = createApp(App)

app.use(ToastService)

Sentry.init({
  app,
  dsn: "https://f139da63d53ee8514c6ec9c7d6153974@sentry.tresfactorial.com/3",
  integrations: [
    // Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  release: VERSION,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

app.config.globalProperties.$theme = theme;
app.config.globalProperties.$permisos = permisos;
app.config.globalProperties.$configuraciones = configuraciones;
app.config.globalProperties.emitter = emitter;
app.config.globalProperties.$pusher = pusher;
app.provide('$pusher', pusher);
app.provide('emitter', emitter);
app.provide('permisos', permisos);
app.provide('configuraciones', configuraciones);
app.provide('theme', theme);

app.config.globalProperties.$formatoNumero = (numero, formato = 'es-MX', decimals = 2) => {
  let numeroFormateado = 0;
  if (numero && numero !== '' && numero !== null && numero !== undefined && !isNaN(numero)) {
    numeroFormateado = Intl.NumberFormat('es-MX', {
      maximumFractionDigits: decimals,
    }).format(numero);
  }
  return numeroFormateado;
};
app.config.globalProperties.$textSubstring = (string, length = 40, showSuspensive = true) => {
  if (string) {
    if (string.length > length) {
      return string.substring(0, length) + (showSuspensive ? '...' : '')
    } else {
      return string
    }
  } else {
    return string;
  }
};
app.config.globalProperties.$formatearFecha = (value, from = 'sql', to = 'dd/MM/yyyy') => {
  if (!value) {
    return '-';
  }
  let fecha;
  if (from === 'sql') {
    fecha = DateTime.fromSQL(value);
  } else if (from === 'iso') {
    fecha = DateTime.fromISO(value);
  } else if (from === 'millis') {
    fecha = DateTime.fromMillis(value);
  } else if (from === 'luxon') {
    fecha = value;
  }
  if (!fecha?.isValid) {
    return '-';
  }
  return fecha.toFormat(to);
};

app.component('paginador', Paginador);
app.component('form-error', FormError);
app.component('flash-alert', FlashAlert);
app.component('dialogo-cargando', DialogoCargando);
app.component('text-counter', LabelTextCounter);

app.use(PrimeVue, {
  pt: {
    dialog: {
      header: {
        class: 'bg-primary-950 rounded-t-xl !px-3 !py-3 text-white'
      },
      content: {
        class: '!pt-3'
      },
      pcCloseButton: {
        root: {
          class: '!text-white hover:!text-primary-800'
        }
      }
    },
    togglebutton: {
      root: {
        class: '!py-1.5 !px-2'
      }
    }
  },
  theme: {
    preset: MyPrimeVuePreset,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark-mode',
      cssLayer: false
    }
  },
  locale: {
    startsWith: 'Empieza con',
    contains: 'Contiene',
    notContains: 'No contiene',
    endsWith: 'Termina con',
    equals: 'Es igual',
    notEquals: 'No es igual',
    noFilter: 'Sin filtro',
    lt: 'Menos a',
    lte: 'Menos o igual a',
    gt: 'Mayor a',
    gte: 'Mayor o igual a',
    dateIs: 'La fecha es',
    dateIsNot: 'La fecha no es',
    dateBefore: 'La fecha es antes de',
    dateAfter: 'La fecha es después de',
    clear: 'Limpiar',
    apply: 'Aplicar',
    matchAll: 'Todo coincide',
    matchAny: 'Cualquier parte coincide',
    addRule: 'Agregar regla',
    removeRule: 'Quitar regla',
    accept: 'Aceptar',
    reject: 'Rechazar',
    choose: 'Escoger',
    upload: 'Subir',
    cancel: 'Cancelar',
    dayNames: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
    dayNamesShort: ["Dom", "Lun", "Mar", "Mier", "Juev", "Vier", "Sab"],
    dayNamesMin: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
    monthNames: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
    monthNamesShort: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
    today: 'Hoy',
    weekHeader: 'Sem',
    firstDayOfWeek: 0,
    dateFormat: 'dd/mm/yy',
    weak: 'Semana',
    medium: 'Medio',
    strong: 'Fuerte',
    passwordPrompt: 'Ingrese la contraseña',
    emptyFilterMessage: 'No hay opciones disponibles',
    emptyMessage: 'No hay opciones disponibles'
  }
});
app.use(createPinia())
app.use(router)
app.use(VueGoogleMaps, {
  load: {
    key: getEnv('VITE_GOOGLE_API_KEY')
  }
})

app.mount('#app')

import Tooltip from 'primevue/tooltip';
import * as Sentry from "@sentry/vue";
import {getEnv} from "@/utils/env";

app.directive('tooltip', Tooltip);
